/**
 * Route registry — single source of truth for every internal route the app
 * serves. Kept in sync manually with `src/App.tsx`. Used by the link-audit
 * tool to detect broken in-app links.
 *
 * Each entry is either a static path or a route pattern with `:param`
 * placeholders. Redirects are tracked separately so the auditor can flag
 * "valid but redirected" links (e.g. /exhibits → /explore?view=stories).
 */

export interface RouteDefinition {
  /** Pattern as registered with React Router (e.g. "/exhibits/:slug"). */
  pattern: string;
  /** Human-readable label for the audit UI. */
  label?: string;
}

export interface RedirectDefinition {
  from: string;
  to: string;
}

/**
 * Static & dynamic route patterns. Order does not matter for matching since
 * we test all patterns; longest-static-prefix wins on ties.
 */
export const ROUTE_PATTERNS: RouteDefinition[] = [
  { pattern: '/' },
  { pattern: '/museum' },
  { pattern: '/explore' },
  { pattern: '/exhibits/schreiner-iii-collection' },
  { pattern: '/exhibits/saddle-history' },
  { pattern: '/exhibits/longhorn-preservation' },
  { pattern: '/exhibits/:slug' },
  { pattern: '/texas-longhorn-heritage' },
  { pattern: '/longhorn-preservation-pioneers' },
  { pattern: '/longhorn-gallery' },
  { pattern: '/historical-gallery' },
  { pattern: '/historical-figures' },
  { pattern: '/tlbaa-founders' },
  { pattern: '/tlbaa-founders/walter-riedel' },
  { pattern: '/tlbaa-founders/travis-marks' },
  { pattern: '/tlbaa-founders/oswald-sauer' },
  { pattern: '/tlbaa-founders/elvin-blevins' },
  { pattern: '/tlbaa-founders/arthur-bright' },
  { pattern: '/tlbaa-founders/walter-b-scott' },
  { pattern: '/tlbaa-growth-timeline' },
  { pattern: '/claude-heck-shrader' },
  { pattern: '/captain-schreiner' },
  { pattern: '/schreiner-iii' },
  { pattern: '/gun-room' },
  { pattern: '/schreiner-iii-firearms' },
  { pattern: '/schreiner-iii-firearms/registry' },
  { pattern: '/schreiner-iii-buyback-list' },
  { pattern: '/charlie-iii-colt-ad-ledger' },
  { pattern: '/schreiner-iii-auction-2003' },
  { pattern: '/schreiner-iii-auction-2003/texas-rangers' },
  { pattern: '/schreiner-iii-auction-2003/catalog-pages' },
  { pattern: '/ria-wanted-lots' },
  { pattern: '/ria-wanted-lots/qr-sheet' },
  { pattern: '/ria-wanted-lots/:lotId' },
  { pattern: '/admin/wanted-lot-status' },
  { pattern: '/admin/ocr-jobs' },
  { pattern: '/galleries/:slug' },
  { pattern: '/admin/galleries' },
  { pattern: '/admin/galleries/:id' },
  { pattern: '/marshall-slover-acquisitions-2026' },
  { pattern: '/marshall-slover-acquisitions-2026/:id' },
  { pattern: '/heritage-acquisitions-import' },
  { pattern: '/heritage-acquisitions-import/:id' },
  { pattern: '/schreiner-dynasty' },
  { pattern: '/schreiner-family-tree' },
  { pattern: '/schreiner-family-photos' },
  { pattern: '/vital-records' },
  { pattern: '/vital-records-wanted' },
  { pattern: '/yo-fiscal-1983' },
  { pattern: '/myrtle-schreiner' },
  { pattern: '/walter-schreiner' },
  { pattern: '/louis-schreiner' },
  { pattern: '/ac-schreiner' },
  { pattern: '/gus-schreiner' },
  { pattern: '/1917-division' },
  { pattern: '/charles-schreiner-jr' },
  { pattern: '/charles-schreiner-iv' },
  { pattern: '/walter-schreiner-iv' },
  { pattern: '/gus-schreiner-iv' },
  { pattern: '/louie-schreiner-ii' },
  { pattern: '/schreiner-family-album' },
  { pattern: '/audrey-schreiner' },
  { pattern: '/marion-vandenburgh' },
  { pattern: '/patricia-ann-lopez' },
  { pattern: '/lena-schreiner' },
  { pattern: '/frances-schreiner' },
  { pattern: '/mimi-schreiner' },
  { pattern: '/madelina-schreiner' },
  { pattern: '/josephine-parker' },
  { pattern: '/ranch-characters' },
  { pattern: '/jesse-garza' },
  { pattern: '/button-forehand' },
  { pattern: '/jerome-alexander' },
  { pattern: '/guides-and-legends' },
  { pattern: '/charlie-seale' },
  { pattern: '/jimmy-peril' },
  { pattern: '/bob-jay-darragh' },
  { pattern: '/david-nesbitt' },
  { pattern: '/louie-mecey' },
  { pattern: '/clyde-fischer' },
  { pattern: '/jack-potter' },
  { pattern: '/harvey-goff' },
  { pattern: '/red-alexander' },
  { pattern: '/photo-search' },
  { pattern: '/document-search' },
  { pattern: '/land-history' },
  { pattern: '/klondike-letter-1898' },
  { pattern: '/yo-guest-brochure-1960s' },
  { pattern: '/headquarters-map-then-and-now' },
  { pattern: '/people' },
  { pattern: '/yo-ranch' },
  { pattern: '/hall-of-fame-portraits' },
  { pattern: '/portrait-wall' },
  { pattern: '/texas-ranger-hall-of-fame' },
  { pattern: '/texas-ranger-hall-of-fame-timeline' },
  { pattern: '/texas-rangers-index' },
  { pattern: '/texas-ranger-eras' },
  { pattern: '/texas-ranger-eras/:decade' },
  { pattern: '/texas-ranger-equipment' },
  { pattern: '/texas-ranger-equipment/:category' },
  { pattern: '/texas-ranger-timeline' },
  { pattern: '/texas-ranger-glossary' },
  { pattern: '/yo-ranch-exhibits' },
  { pattern: '/schreiner-generations' },
  { pattern: '/photo-exhibit' },
  { pattern: '/photo-gallery' },
  { pattern: '/archive-photographs' },
  { pattern: '/ranch-photograph-gallery' },
  { pattern: '/yo-ranch-photographs' },
  { pattern: '/artifacts' },
  { pattern: '/face-gallery' },
  { pattern: '/reference-gallery' },
  { pattern: '/identified-people', label: 'People identified in the archive' },
  { pattern: '/person-timeline/:slug', label: 'Person timeline' },
  { pattern: '/person-identifications/:slug' },
  { pattern: '/face-gallery/:faceId' },
  { pattern: '/admin/face-name-conflicts' },
  { pattern: '/admin/visitor-biographies' },
  { pattern: '/admin/visitor-vitals' },

  { pattern: '/ranger-photograph-timeline' },
  { pattern: '/schreiner-family/:slug' },
  { pattern: '/share-your-story' },
  { pattern: '/admin/guest-stories' },
  { pattern: '/admin/person-portraits' },
  { pattern: '/ranch-timeline' },
  { pattern: '/people/:slug' },
  { pattern: '/admin/research-photo-id' },
  { pattern: '/land-history/archive' },
  { pattern: '/land-history/deeds' },
  { pattern: '/land-history/:tractKey' },
  { pattern: '/yo-historic-land-documents' },
  { pattern: '/yo-historic-land-documents/registry' },
  { pattern: '/yo-historic-land-documents/deeds/:slug' },
  { pattern: '/yo-historic-land-documents/map' },
  { pattern: '/yo-historic-land-documents/timeline' },
  { pattern: '/yo-historic-land-documents/people' },
  { pattern: '/yo-historic-land-documents/tasks' },
  { pattern: '/yo-historic-land-documents/gaps' },
  { pattern: '/yo-historic-land-documents/documents' },
  { pattern: '/yo-historic-land-documents/resources' },
  { pattern: '/yo-historic-land-documents/duplicates' },
  { pattern: '/yo-historic-land-documents/maps' },
  { pattern: '/yo-historic-land-documents/maps/:slug' },
  { pattern: '/yo-historic-land-documents/qa/tract-extract' },
  { pattern: '/yo-historic-land-documents/qa/deed-links' },
  { pattern: '/yo-historic-land-documents/qa/drive-audit' },
  { pattern: '/yo-historic-land-documents/qa/drive-ingest' },
  { pattern: '/yo-historic-land-documents/qa/drive-mirror' },
  { pattern: '/yo-historic-land-documents/qa/link-orphans' },
  { pattern: '/yo-historic-land-documents/qa/page-ranges' },
  { pattern: '/yo-historic-land-documents/qa/split-rename' },
  { pattern: '/yo-historic-land-documents/research-queue' },
  { pattern: '/yo-historic-land-documents/tracts' },
  { pattern: '/yo-historic-land-documents/tracts/review' },
  { pattern: '/yo-historic-land-documents/tracts/unmatched' },

  { pattern: '/clarence-hyde' },
  { pattern: '/captain-bob-snow' },
  { pattern: '/adam-carter' },
  { pattern: '/garland-scogin' },
  { pattern: '/mac-hyde' },
  { pattern: '/garza-family' },
  { pattern: '/dr-russell-thomas' },
  { pattern: '/virgil-rodgers' },
  { pattern: '/arthur-cate' },
  { pattern: '/george-hey' },
  { pattern: '/warren-klein' },
  { pattern: '/james-a-gray' },
  { pattern: '/zach-sager' },
  { pattern: '/bertie-verner' },
  { pattern: '/hal-swiggett' },
  { pattern: '/exotic-species' },
  { pattern: '/exotic-species/giraffe' },
  { pattern: '/exotic-species/yo-ibex' },
  { pattern: '/exotic-species/:id' },
  { pattern: '/wildlife-heritage' },
  { pattern: '/native-american-heritage' },
  { pattern: '/exotic-wildlife' },
  { pattern: '/exotic-wildlife-program' },
  { pattern: '/exotic-wildlife-association' },
  { pattern: '/d-michael-hughes' },
  { pattern: '/broken-arrow-ranch' },
  { pattern: '/karl-kinsel' },
  { pattern: '/david-rickenbacker' },
  { pattern: '/outdoor-awareness-programs' },
  { pattern: '/cal-newnam' },
  { pattern: '/jim-isleib' },
  { pattern: '/diane-isleib' },
  { pattern: '/james-rice-outdoor-awareness' },
  { pattern: '/larry-thornton' },
  { pattern: '/jeff-slomka' },
  { pattern: '/gaye-slomka' },
  { pattern: '/robert-maxson' },
  { pattern: '/garnet-brooks' },
  { pattern: '/wildlife-safari' },
  { pattern: '/hunting-heritage' },
  { pattern: '/trophy-records-hunters' },
  { pattern: '/famous-visitors' },
  { pattern: '/visitor-timeline' },
  { pattern: '/visitor-face-timeline', label: 'Visitors in the record' },
  { pattern: '/visitor-gallery' },
  { pattern: '/visitor-map', label: 'Visitor map' },
  { pattern: '/visitor-directory', label: 'Every guest' },
  { pattern: '/document-gallery', label: 'Document gallery' },
  { pattern: '/visitors/:slug', label: 'Visitor biography' },
  { pattern: '/visitors/:slug/timeline', label: 'Visitor timeline' },
  { pattern: '/visitor-exhibits' },
  { pattern: '/astronaut-room' },
  { pattern: '/joe-engle' },
  { pattern: '/chuck-yeager' },
  { pattern: '/doug-walker' },
  { pattern: '/ron-schara' },
  { pattern: '/norman-schwarzkopf' },
  { pattern: '/lyndon-johnson' },
  { pattern: '/john-connally' },
  { pattern: '/dolph-briscoe' },
  { pattern: '/ace-reid' },
  { pattern: '/joe-beeler' },
  { pattern: '/fred-gipson' },
  { pattern: '/hondo-crouch' },
  { pattern: '/hollywood-media' },
  { pattern: '/news-media' },
  { pattern: '/literary-legacy' },
  { pattern: '/j-frank-dobie' },
  { pattern: '/james-michener' },
  { pattern: '/elliott-means' },
  { pattern: '/michael-wayne' },
  { pattern: '/patrick-wayne' },
  { pattern: '/audie-murphy' },
  { pattern: '/thomas-haden-church' },
  { pattern: '/literary-legacy/tlj-yo-ranch' },
  { pattern: '/literary-legacy/yo-once-in-a-while' },
  { pattern: '/literary-legacy/rangelands-yo-ranch-1984' },
  { pattern: '/culture-and-fame' },
  { pattern: '/yo-sam-houston' },
  { pattern: '/yo-carmela' },
  { pattern: '/bevo-vii' },
  { pattern: '/heritage' },
  { pattern: '/yo-ranch-heritage' },
  { pattern: '/heritage/:slug' },
  { pattern: '/stewardship' },
  { pattern: '/collections/firearms' },
  { pattern: '/collections/firearms-evolution' },
  { pattern: '/colt-model-1878-scholarship' },
  { pattern: '/aten-brothers' },
  { pattern: '/h-clay-dahlberg' },
  { pattern: '/collections/:type' },
  { pattern: '/texas-rangers' },
  { pattern: '/texas-rangers/crowder' },
  { pattern: '/texas-rangers/jackson' },
  { pattern: '/texas-rangers/gonzaullas' },
  { pattern: '/texas-rangers/dean' },
  { pattern: '/texas-rangers/krumnow' },
  { pattern: '/texas-rangers/odom' },
  { pattern: '/texas-rangers/gillett' },
  { pattern: '/texas-rangers/hamer' },
  { pattern: '/texas-rangers/hickman' },
  { pattern: '/texas-rangers/allee' },
  { pattern: '/captain-hamer' },
  { pattern: '/captain-hickman' },
  { pattern: '/captain-allee' },
  { pattern: '/captain-gonzaullas' },
  { pattern: '/captain-jackson' },
  { pattern: '/captain-crowder' },
  { pattern: '/captain-dean' },
  { pattern: '/captain-gillett' },
  { pattern: '/captain-fox' },
  { pattern: '/captain-aldrich' },
  { pattern: '/captain-coffee' },
  { pattern: '/captain-klevenhagen' },
  { pattern: '/texas-rangers/klevenhagen' },
  { pattern: '/captain-james-rogers' },
  { pattern: '/texas-rangers/james-rogers' },
  { pattern: '/captain-mcculloch' },
  { pattern: '/texas-rangers/mcculloch' },
  { pattern: '/texas-rangers/fox' },
  { pattern: '/texas-rangers/aldrich' },
  { pattern: '/texas-rangers/coffee' },
  { pattern: '/texas-rangers/canales' },
  { pattern: '/texas-rangers/puckett' },
  { pattern: '/ranger-clifton' },
  { pattern: '/texas-rangers/clifton' },
  { pattern: '/ranger-press-clippings' },
  { pattern: '/ranger-press-clippings/:slug' },
  { pattern: '/texas-ranger-hall' },
  { pattern: '/ranger-organizational-history' },
  { pattern: '/jules-baker' },
  { pattern: '/henry-ransom' },
  { pattern: '/senator-hawley' },
  { pattern: '/johnson-fry-collection' },
  { pattern: '/ranger-badge-evolution' },
  { pattern: '/frontier-legends' },
  { pattern: '/texas-rangers/hays' },
  { pattern: '/texas-rangers/mcnelly' },
  { pattern: '/texas-rangers/hughes' },
  { pattern: '/texas-rangers/walker' },
  { pattern: '/ranch-history' },
  { pattern: '/ranch-landmarks' },
  { pattern: '/camp-verde-csa' },
  { pattern: '/schreiner-booklet' },
  { pattern: '/schreiner-bowlin-pamphlet' },
  { pattern: '/von-rosenberg-letters' },
  { pattern: '/von-rosenberg-letters/:slug' },
  { pattern: '/schreiner-university' },
  { pattern: '/schreiner-properties' },
  { pattern: '/schreiner-properties-map' },
  { pattern: '/alfred-giles' },
  { pattern: '/historic-hill-country-structures' },
  { pattern: '/schreiner-mercantile' },
  { pattern: '/schreiner-philanthropy' },
  { pattern: '/schreiner-alamo-preservation' },
  { pattern: '/schreiner-bank-heritage' },
  { pattern: '/yo-social-club' },
  { pattern: '/photo-comparison-1920s' },
  { pattern: '/yo-ranch-centennial' },
  { pattern: '/yo-ranch-hotel-loan-collection' },
  { pattern: '/rusty-cox' },
  { pattern: '/ranch-map' },
  { pattern: '/visit' },
  { pattern: '/visit/status' },
  { pattern: '/contact' },
  { pattern: '/artifacts/:slug' },
  { pattern: '/artifacts' },
  { pattern: '/artifact/:id' },
  { pattern: '/admin/archive-to-cms' },
  { pattern: '/admin/exclusions' },
  { pattern: '/admin/setup' },
  { pattern: '/admin/guests' },
  { pattern: '/login' },
  { pattern: '/reset-password' },
  { pattern: '/admin/test-login' },
  { pattern: '/admin/content-indexing' },
  { pattern: '/admin/firearm-tips' },
  { pattern: '/admin/photo-tips' },
  { pattern: '/admin/image-dashboard' },
  { pattern: '/admin/unlinked-uploads' },
  { pattern: '/admin/uploads-inbox' },
  { pattern: '/admin/land-record-matches' },
  { pattern: '/admin/security-report' },
  { pattern: '/admin/link-audit' },
  { pattern: '/admin/broken-links' },
  { pattern: '/admin/not-found-log' },
  { pattern: '/admin/artifact-notes' },
  { pattern: '/admin/biographies' },
  { pattern: '/admin/doc-import-triage' },
  { pattern: '/archive/doc-import/:folder/reader' },
  { pattern: '/admin/firearm-exhibit-links' },
  { pattern: '/admin/photo-identity' },
  { pattern: '/admin/arcface-review' },
  { pattern: '/admin/face-work-queue' },
  { pattern: '/admin/photo-work-queue' },
  { pattern: '/admin/identity-review' },
  { pattern: '/admin/tagging' },
  { pattern: '/admin/caption-tagging' },
  { pattern: '/admin' },
  { pattern: '/admin/login' },
  { pattern: '/admin/reset-password' },
  { pattern: '/admin/archive' },
  { pattern: '/admin/archive-review-queue' },
  { pattern: '/admin/analyze-images' },
  { pattern: '/admin/image-descriptions' },
  { pattern: '/admin/contact-submissions' },
  { pattern: '/admin/appointments' },
  { pattern: '/admin/duplicates' },
  { pattern: '/admin/merge-log' },
  { pattern: '/admin/artifact-dedup-debug' },
  { pattern: '/admin/deed-review' },
  { pattern: '/admin/deed-review/:slug' },
  { pattern: '/admin/deed-integrity' },
  { pattern: '/admin/scan-text-index' },
  { pattern: '/admin/qr-labels' },
  { pattern: '/admin/auction-firearm-links' },
  { pattern: '/admin/auction-firearm-review' },
  { pattern: '/admin/longhorn-match-audit' },
  { pattern: '/admin/collection-match-audit' },
  { pattern: '/admin/false-positives' },
  { pattern: '/admin/review-queue' },
  { pattern: '/admin/tag-conflicts' },
  { pattern: '/admin/heritage' },
  { pattern: '/admin/cms' },
  { pattern: '/admin/ocr-review' },
  { pattern: '/admin/ledger-review' },
  { pattern: '/admin/photo-review' },
  { pattern: '/admin/photo-research' },
  { pattern: '/admin/photo-upload' },
  { pattern: '/admin/photo-archive' },
  { pattern: '/admin/photo-archive/review-queue' },
  { pattern: '/admin/photo-archive/clusters' },
  { pattern: '/admin/photo-archive/clusters/:id' },
  { pattern: '/admin/photo-archive/alert-log' },
  { pattern: '/admin/captain-portraits' },
  { pattern: '/admin/gallery-credit-audit' },
  { pattern: '/admin/gun-room-gallery' },
  { pattern: '/admin/artifact-gallery-images' },
  { pattern: '/admin/historic-photos' },
  { pattern: '/admin/ocr-quality-audit' },
  { pattern: '/admin/ocr-requeue' },
  { pattern: '/admin/archive/pages-backfill' },
  { pattern: '/admin/acquisitions' },
  { pattern: '/admin/acquisitions/flag-queue' },
  { pattern: '/admin/acquisitions/bulk-corrections' },
  { pattern: '/admin/acquisitions/auto-image-backfill' },
  { pattern: '/admin/acquisitions/missing-photos' },
  { pattern: '/archive' },
  { pattern: '/archive/:slug' },
  { pattern: '/on-display' },
  { pattern: '/ranch-found-photographs' },
  { pattern: '/saddle-room-archive' },
  { pattern: '/photos/:slug' },
  { pattern: '/schreiner-iii-auction-2003/coverage' },
  { pattern: '/request-appointment' },
];

/**
 * Server-side redirects defined as <Navigate> elements in App.tsx.
 * Links pointing here are valid but should be flagged so they can be
 * updated to the canonical destination.
 */
export const ROUTE_REDIRECTS: RedirectDefinition[] = [
  { from: '/exhibits', to: '/explore?view=stories' },
  { from: '/exhibits/frontier-justice', to: '/texas-ranger-hall' },
  { from: '/collections', to: '/explore?view=types' },
  { from: '/content-search', to: '/document-search' },
  // Legacy slug redirects — keep in sync with src/App.tsx.
  { from: '/firearms-collection', to: '/collections/firearms' },
  { from: '/ranger-firearms-evolution', to: '/collections/firearms-evolution' },
  { from: '/tlj-yo-ranch-article', to: '/literary-legacy/tlj-yo-ranch' },
  { from: '/yo-once-in-a-while-publication', to: '/literary-legacy/yo-once-in-a-while' },
  { from: '/rangelands-yo-ranch-1984', to: '/literary-legacy/rangelands-yo-ranch-1984' },
  { from: '/charles-schreiner-iii', to: '/schreiner-iii' },
  // Legacy wildlife/exotic aliases (kept in sync with src/App.tsx).
  { from: '/wildlife', to: '/wildlife-heritage' },
  { from: '/exotics', to: '/exotic-species' },
  { from: '/exotic-game', to: '/exotic-species' },
  { from: '/exotic-animals', to: '/exotic-species' },
  { from: '/exotic-hunting', to: '/exotic-species' },
  { from: '/wildlife-program', to: '/exotic-wildlife-program' },
  { from: '/exotic-program', to: '/exotic-wildlife-program' },
  { from: '/ewa', to: '/exotic-wildlife-association' },
  { from: '/exotic-wildlife-heritage', to: '/wildlife-heritage' },
  { from: '/conservation', to: '/wildlife-heritage' },
  // Broken-nav backfill (see App.tsx for the matching <Route> entries).
  { from: '/yo-historical-archive', to: '/archive' },
  { from: '/admin/appointment-requests', to: '/admin/appointments' },
  { from: '/ranger-krumnow', to: '/texas-rangers/krumnow' },
  { from: '/topics/texas-rangers', to: '/texas-rangers' },
  { from: '/topics/texas-longhorns', to: '/texas-longhorn-heritage' },
  { from: '/topics/famous-visitors', to: '/famous-visitors' },
  { from: '/topics/exotic-wildlife-heritage', to: '/wildlife-heritage' },
  { from: '/texas-rangers/captain-aldrich', to: '/captain-aldrich' },
  { from: '/texas-rangers/arrington', to: '/texas-rangers' },
  { from: '/texas-rangers/peoples', to: '/texas-rangers' },
  { from: '/longhorn-preservation', to: '/longhorn-preservation-pioneers' },
  { from: '/texas-longhorns', to: '/texas-longhorn-heritage' },
  { from: '/hollywood-and-media', to: '/hollywood-media' },
  { from: '/culture-fame', to: '/culture-and-fame' },
  { from: '/ranch-found-photos', to: '/ranch-found-photographs' },
  { from: '/land-records-surveys', to: '/yo-historic-land-documents' },
  { from: '/collections/historic-firearms', to: '/collections/firearms' },
  { from: '/collections/texas-ranger-artifacts', to: '/texas-ranger-hall' },
  { from: '/collections/spurs-leather-frontier', to: '/explore?view=types' },
];

/**
 * Wildcard "catch-all" base paths handled by LegacyExoticRedirect. Any URL
 * starting with one of these prefixes resolves to the legacy redirect page
 * (which then routes the user to the best new landing page).
 *
 * Kept in sync with the `path="/.../*"` routes in src/App.tsx.
 */
export const ROUTE_WILDCARDS: string[] = [
  '/exotic-wildlife',
  '/exotic-wildlife-program',
  '/exotic-wildlife-association',
  '/wildlife-heritage',
  '/exotics',
  '/wildlife',
  '/exotic',
  '/exotic-game',
  '/exotic-animals',
  '/exotic-hunting',
  '/exotic-program',
  '/wildlife-program',
  '/ewa',
];

/**
 * Convert a route pattern like "/exotic-species/:id" to a regex that
 * matches any concrete URL of the same shape.
 */
function patternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        // Param segments match any non-slash, non-empty value.
        return '[^/]+';
      }
      // Escape regex metacharacters in literal segments.
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return new RegExp(`^${escaped}$`);
}

const COMPILED_PATTERNS = ROUTE_PATTERNS.map((r) => ({
  ...r,
  regex: patternToRegex(r.pattern),
}));

const REDIRECT_PATHS = new Set(ROUTE_REDIRECTS.map((r) => r.from));

/**
 * URL prefixes for static files shipped from /public (PDFs, images, sitemaps,
 * videos, etc.). Any internal link starting with one of these is treated as
 * a valid static-asset reference rather than a broken route.
 *
 * Keep in sync with the top-level directories that exist under /public.
 */
const PUBLIC_ASSET_PREFIXES: string[] = [
  '/documents/',
  '/archives/',
  '/heritage/',
  '/artifacts/',
  '/images/',
  '/videos/',
  '/og/',
  '/assets/',
  '/pdfjs/',
];

/** Specific static files at the /public root that the auditor should know about. */
const PUBLIC_ASSET_FILES: Set<string> = new Set([
  '/favicon.ico',
  '/apple-touch-icon.png',
  '/icon-192.png',
  '/icon-512.png',
  '/placeholder.svg',
  '/robots.txt',
  '/site.webmanifest',
  '/sitemap.xml',
  '/sitemap-acquisitions.xml',
  '/sitemap-artifacts.xml',
  '/sitemap-collections.xml',
  '/sitemap-exhibits.xml',
  '/sitemap-pages.xml',
]);

export type LinkStatus = 'valid' | 'redirect' | 'broken' | 'external' | 'anchor';

export interface MatchResult {
  status: LinkStatus;
  /** Matched route pattern, if any. */
  pattern?: string;
  /** Redirect destination, if status === 'redirect'. */
  redirectTo?: string;
}

/**
 * Strip query string and hash from a path, returning just the pathname
 * portion that React Router uses to match routes.
 */
function pathnameOnly(href: string): string {
  const queryIdx = href.indexOf('?');
  const hashIdx = href.indexOf('#');
  let end = href.length;
  if (queryIdx !== -1) end = Math.min(end, queryIdx);
  if (hashIdx !== -1) end = Math.min(end, hashIdx);
  return href.slice(0, end);
}

/**
 * Classify an internal link string against the registry.
 */
export function matchRoute(href: string): MatchResult {
  // External links (mailto:, tel:, http(s)://, etc.) are not in scope.
  if (/^(https?:|mailto:|tel:|ftp:)/i.test(href)) {
    return { status: 'external' };
  }
  // Pure anchor / same-page link.
  if (href.startsWith('#')) {
    return { status: 'anchor' };
  }
  // Normalize: only consider absolute paths starting with "/".
  if (!href.startsWith('/')) {
    // Relative paths are unusual in this codebase; treat as broken.
    return { status: 'broken' };
  }

  const path = pathnameOnly(href);

  if (REDIRECT_PATHS.has(path)) {
    const redirect = ROUTE_REDIRECTS.find((r) => r.from === path);
    return {
      status: 'redirect',
      pattern: path,
      redirectTo: redirect?.to,
    };
  }

  // Static files shipped from /public (PDFs, images, sitemaps, videos, etc.).
  if (
    PUBLIC_ASSET_FILES.has(path) ||
    PUBLIC_ASSET_PREFIXES.some((prefix) => path.startsWith(prefix))
  ) {
    return { status: 'valid', pattern: path };
  }

  // Prefer exact static matches before dynamic ones for clearer reporting.
  const staticMatch = COMPILED_PATTERNS.find(
    (r) => !r.pattern.includes(':') && r.pattern === path,
  );
  if (staticMatch) {
    return { status: 'valid', pattern: staticMatch.pattern };
  }

  const dynamicMatch = COMPILED_PATTERNS.find((r) => r.regex.test(path));
  if (dynamicMatch) {
    return { status: 'valid', pattern: dynamicMatch.pattern };
  }

  // Wildcard catch-all prefixes (handled by LegacyExoticRedirect).
  const wildcard = ROUTE_WILDCARDS.find(
    (base) => path === base || path.startsWith(`${base}/`),
  );
  if (wildcard) {
    return { status: 'redirect', pattern: `${wildcard}/*`, redirectTo: wildcard };
  }

  return { status: 'broken' };
}
